import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'percentFormat',
  standalone: true
})
export class PercentFormatPipe implements PipeTransform {

  /**
   * Transforms a decimal proportion (e.g., 0.1458) into a formatted percentage string.
   *
   * - Multiplies the input value by 100 to convert it to a percentage.
   * - If the resulting percentage is a whole number (e.g., 0, 10, 25), it is displayed without decimals.
   * - If the result has decimal places, up to 2 digits are shown, with trailing zeros removed.
   *
   * This ensures clean output:
   *   - `0` → `"0%"`
   *   - `0.1` → `"10%"`
   *   - `0.147` → `"14.7%"`
   *   - `0.1458` → `"14.58%"`
   *
   * @param value - The input number as a decimal proportion (must be a finite number).
   *                For example: `0.1` represents 10%, `0.1458` represents 14.58%.
   *
   * @returns A string representing the percentage without unnecessary decimal places.
   *          Returns `"NaN"` if the input is not a valid number.
   *
   * @example
   *   transform(0.1);     // returns "10"
   *   transform(0.147);   // returns "14.7"
   *   transform(0.1458);  // returns "14.58"
   *   transform(0);       // returns "0"
   *
   * @remarks
   * Uses `Number.isInteger()` to detect whole numbers and `.toFixed(2)` with regex trimming
   * to remove insignificant trailing zeros after the decimal point.
   */
  transform(value: number | string): string {
    // Handle null, undefined, or empty string
    if (value == null || value === '') {
      return '0';
    }
    // Convert string to number
    const num = typeof value === 'string' ? parseFloat(value) : value;

    // Validate if it's a finite number
    if (typeof num !== 'number' || !isFinite(num)) {
      return 'NaN';
    }
    const percent = num * 100;

    if (Number.isInteger(percent)) {
      return percent.toString();
    }
    return percent.toFixed(2).replace(/\.?0+$/, '');
  }

}
